'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import ProgramCard from './ProgramCard';
import ProgramFilter from './ProgramFilter';
import ProgramDetailModal from './ProgramDetailModal';
import ComparisonMatrix from './ComparisonMatrix';
import QuestionnaireModal from './QuestionnaireModal';

interface Program {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  ageGroup: string;
  duration: string;
  level: string;
  features: string[];
  price: string;
  icon: string;
  category: string;
  detailedDescription: string;
  methodology: string[];
  schedule: string;
  coachName: string;
  coachCertification: string;
  equipment: string[];
  safetyProtocols: string[];
}

const ProgramsInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedAge, setSelectedAge] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedGoal, setSelectedGoal] = useState('all');
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
  const [recommendedProgram, setRecommendedProgram] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const programs: Program[] = [
  {
    id: 1,
    title: 'Speed & Strength Training',
    description: 'Comprehensive athletic development program merging elite speed training with strength and conditioning. Build explosive power, improve acceleration, and develop functional strength through Olympic methodologies and cutting-edge biomechanics.',
    image: "/assets/images/PHOTO-2024-11-16-20-20-02-1770454700441.jpg",
    alt: 'Athletic young man in black sportswear sprinting on outdoor track with intense focus and proper running form',
    ageGroup: 'Youth & Teens (10+)',
    duration: '1.5-2 hrs',
    level: 'Intermediate to Advanced',
    features: [
    'Olympic-level sprint mechanics training',
    'Plyometric power development',
    'Strength and conditioning integration',
    'Agility ladder and cone drills',
    'Video analysis and technique correction',
    'Competition preparation strategies'],

    price: '₹18,000/month',
    icon: 'BoltIcon',
    category: 'speed',
    detailedDescription: 'Our flagship Speed & Strength Training program combines the best of athletics training with comprehensive strength and conditioning. Led by elite coaches Simran Kaur, known as the fastest girl in Delhi with expertise in sprints and hurdles, and Somvir, a national champion specializing in pole vault and javelin throw. This program merges biomechanical analysis, progressive overload principles, and sport-specific conditioning to develop explosive speed, power, and athletic performance. Perfect for athletes seeking to maximize their potential through structured progression and expert coaching.',
    methodology: [
    'Phase 1: Movement pattern assessment and baseline testing',
    'Phase 2: Technique refinement with video analysis',
    'Phase 3: Power development through plyometrics and strength work',
    'Phase 4: Speed endurance and competition simulation',
    'Ongoing: Performance tracking and program optimization'],

    schedule: 'Monday to Saturday - Evening sessions',
    coachName: 'Simran Kaur & Somvir',
    coachCertification: 'Simran Kaur - Fastest Girl of Delhi (Sprints & Hurdles Expert) | Somvir - National Champion (Pole Vault & Javelin Throw Specialist)',
    equipment: [
    'Sprint timing gates and laser systems',
    'Agility ladders and cone sets',
    'Olympic barbells and bumper plates',
    'Resistance bands and parachutes',
    'Video analysis equipment',
    'Power racks and functional training equipment',
    'Olympic-grade track surface'],

    safetyProtocols: [
    'Comprehensive warm-up protocols',
    'Progressive intensity management',
    'Injury prevention screening',
    'Emergency medical support on-site',
    'Insurance coverage included']

  },
  {
    id: 2,
    title: 'Go Speed Field Performance Track',
    description: 'Elite technical events training program specializing in throws, jumps, and pole vault. Master the biomechanics of javelin, shot put, discus, long jump, high jump, and pole vault under the guidance of national champions with proven competition success.',
    image: "https://images.unsplash.com/photo-1528474495462-b16cd84a0cf0",
    alt: 'Athlete performing pole vault technique with proper form against blue sky, demonstrating technical field event mastery',
    ageGroup: 'Youth & Adults (10+)',
    duration: '1.5-2 hrs',
    level: 'Intermediate to Advanced',
    features: [
    'Specialized throws training (javelin, shot put, discus)',
    'Jumps technique development (long jump, high jump)',
    'Pole vault technical mastery',
    'Biomechanical analysis and video review',
    'Competition strategy and mental preparation',
    'Strength and power development for field events'],

    price: '₹18,000/month',
    icon: 'FireIcon',
    category: 'strength',
    detailedDescription: 'The Go Speed Field Performance Track program is designed for athletes seeking to excel in technical field events including throws, jumps, and pole vault. Led by Somvir, a national champion with specialized skills in pole vault and javelin throw whose experience at the highest levels of competition helps athletes refine their technique and performance, and Ajay Kumar, a decorated jumps specialist with multiple gold, silver, and bronze medals including 🥇 Long Jump Inter-college University of Delhi 2017, 🥇 High Jump 77th Delhi State Annual Athletics Meet 2017, 🥈 High Jump Delhi State 2017, 🥈 Long Jump Delhi State 2021, 🥉 High Jump Delhi State 2018, 🥉 High Jump Delhi State 2021, and 🥉 High Jump Open Delhi State Athletics Championship 2022. This program combines technical precision with explosive power development to create elite field event athletes.',
    methodology: [
    'Phase 1: Technical assessment and event-specific fundamentals',
    'Phase 2: Biomechanical refinement with video analysis',
    'Phase 3: Power and explosiveness development',
    'Phase 4: Competition preparation and performance optimization',
    'Ongoing: Advanced technique mastery and personal records'],

    schedule: 'Monday to Saturday - Evening sessions',
    coachName: 'Somvir & Ajay Kumar',
    coachCertification: 'Somvir - National Champion (Pole Vault & Javelin Throw Specialist) | Ajay Kumar - Multiple Gold, Silver & Bronze Medalist (Long Jump & High Jump Expert)',
    equipment: [
    'Pole vault equipment and landing mats',
    'Javelin, shot put, and discus implements',
    'High jump and long jump facilities',
    'Video analysis equipment',
    'Plyometric training tools',
    'Strength and power development equipment',
    'Competition-grade field event surfaces'],

    safetyProtocols: [
    'Proper technique instruction and progression',
    'Equipment safety checks and maintenance',
    'Landing area safety protocols',
    'Injury prevention screening',
    'Emergency medical support on-site']

  },
  {
    id: 3,
    title: 'General Fitness Program',
    description: 'Holistic wellness program combining cardiovascular conditioning, strength training, and flexibility work. Designed for sustainable health improvements and lifelong fitness habits with balanced, enjoyable workouts.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b83293f3-1766376869491.png",
    alt: 'Diverse group of adults in colorful workout attire doing yoga poses on mats in bright studio with natural lighting',
    ageGroup: 'Adults (18-54)',
    duration: 'Ongoing',
    level: 'Beginner to Intermediate',
    features: [
    'Balanced cardio and strength training',
    'Flexibility and mobility sessions',
    'Nutrition guidance and meal planning',
    'Stress management techniques',
    'Community support and motivation'],

    price: '₹12,000/month',
    icon: 'HeartIcon',
    category: 'fitness',
    detailedDescription: 'The General Fitness Program is designed for individuals seeking comprehensive health improvements without the intensity of competitive training. This balanced approach combines cardiovascular fitness, functional strength, flexibility, and wellness education to create sustainable lifestyle changes. Perfect for busy professionals and anyone looking to establish or maintain a healthy, active lifestyle with expert guidance and community support.',
    methodology: [
    'Week 1-4: Habit formation and baseline fitness',
    'Week 5-8: Progressive intensity and variety',
    'Week 9-12: Skill development and confidence building',
    'Ongoing: Sustainable fitness maintenance',
    'Monthly: Progress assessments and goal setting'],

    schedule: 'Monday to Friday - 6:00 PM to 8:00 PM',
    coachName: 'Amit Patel',
    coachCertification: 'ACE Certified Personal Trainer | Nutrition Coach',
    equipment: [
    'Cardio machines (treadmills, bikes, rowers)',
    'Free weights and resistance equipment',
    'Yoga mats and flexibility tools',
    'Heart rate monitors',
    'Body composition analyzers'],

    safetyProtocols: [
    'Health screening and clearance',
    'Gradual progression protocols',
    'Proper form instruction',
    'Hydration and recovery guidance',
    'Medical emergency procedures']

  },
  {
    id: 4,
    title: 'Kids Foundation Program',
    description: 'Age-appropriate athletic development program building fundamental movement skills, coordination, and confidence. Fun, engaging sessions that instill lifelong fitness habits while developing physical literacy and social skills.',
    image: "/assets/images/tempImagesUMsCM-2-3-1770454557293.jpg",
    alt: 'Young athletes with coach at medal ceremony, wearing medals and holding trophies after successful competition at stadium',
    ageGroup: 'Kids (6-10)',
    duration: '1.5 hrs',
    level: 'Beginner',
    features: [
    'Fundamental movement skill development',
    'Age-appropriate strength and conditioning',
    'Coordination and balance training',
    'Team building and social skills',
    'Character development through sports'],

    price: '₹10,000/month',
    icon: 'SparklesIcon',
    category: 'kids',
    detailedDescription: 'The Kids Foundation Program is specifically designed to develop physical literacy and athletic fundamentals in a fun, supportive environment. Using age-appropriate progressions and game-based learning, we build strength, coordination, and confidence while instilling values of discipline, teamwork, and perseverance. Our approach focuses on long-term athletic development rather than early specialization, creating well-rounded young athletes with a love for movement. Led by Kanchan Mathpal, recognized as the best athlete in Delhi and NCR, who excels in sprints and has a strong background in managing young athletes. Her expertise ensures that our programs cater effectively to both competitive athletes and young beginners.',
    methodology: [
    'Phase 1: Movement assessment and skill introduction',
    'Phase 2: Fundamental movement pattern development',
    'Phase 3: Coordination and agility progression',
    'Phase 4: Introduction to sport-specific skills',
    'Ongoing: Character development and goal setting'],

    schedule: 'Mon, Tue, Wed, Fri, Sat - Evening sessions',
    coachName: 'Kanchan Mathpal',
    coachCertification: 'Kids Training Specialist | Best Athlete of Delhi & NCR',
    equipment: [
    'Age-appropriate training equipment',
    'Agility and coordination tools',
    'Soft medicine balls and resistance bands',
    'Balance and stability equipment',
    'Fun, engaging training props'],

    safetyProtocols: [
    'Parental consent and health screening',
    'Age-appropriate exercise selection',
    'Constant supervision and small group sizes',
    'Child protection policies',
    'Emergency contact procedures']

  },
  {
    id: 5,
    title: 'Senior Fitness Initiative',
    description: 'Safe, effective fitness program for active seniors focused on maintaining independence, mobility, and quality of life. Low-impact exercises with emphasis on balance, strength, and functional movement.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bbff1bc1-1767708668012.png",
    alt: 'Active senior couple in comfortable workout clothes doing gentle stretching exercises together in peaceful park setting',
    ageGroup: 'Seniors (55+)',
    duration: 'Ongoing',
    level: 'Beginner to Intermediate',
    features: [
    'Fall prevention and balance training',
    'Joint-friendly strength exercises',
    'Flexibility and mobility work',
    'Social engagement and community',
    'Chronic condition management support'],

    price: '₹8,000/month',
    icon: 'UserGroupIcon',
    category: 'fitness',
    detailedDescription: 'The Senior Fitness Initiative is designed to help active seniors maintain independence, mobility, and quality of life through safe, effective exercise. Our program focuses on functional fitness that translates to daily activities, fall prevention, and chronic condition management. With a supportive community atmosphere and expert guidance, we help seniors stay active, healthy, and engaged in life.',
    methodology: [
    'Initial: Comprehensive health and mobility assessment',
    'Phase 1: Foundation building and confidence development',
    'Phase 2: Progressive strength and balance training',
    'Phase 3: Functional fitness and independence',
    'Ongoing: Maintenance and social engagement'],

    schedule: 'Tuesday, Thursday - 10:00 AM to 11:30 AM',
    coachName: 'Dr. Meera Krishnan',
    coachCertification: 'Senior Fitness Specialist | Physical Therapy Background',
    equipment: [
    'Low-impact cardio equipment',
    'Light resistance bands and weights',
    'Balance and stability tools',
    'Comfortable seating and support',
    'Accessibility-focused facility design'],

    safetyProtocols: [
    'Medical clearance required',
    'Blood pressure monitoring',
    'Emergency response procedures',
    'Individualized exercise modifications',
    'Healthcare provider communication']

  }];


  const filteredPrograms = programs.filter((program) => {
    const ageMatch = selectedAge === 'all' ||
    selectedAge === 'kids' && program.ageGroup.includes('Kids') ||
    selectedAge === 'teens' && program.ageGroup.includes('Teens') ||
    selectedAge === 'adults' && program.ageGroup.includes('Adults') && !program.ageGroup.includes('Seniors') ||
    selectedAge === 'seniors' && program.ageGroup.includes('Seniors');

    const levelMatch = selectedLevel === 'all' ||
    program.level.toLowerCase().includes(selectedLevel.toLowerCase());

    const goalMatch = selectedGoal === 'all' || program.category === selectedGoal;

    return ageMatch && levelMatch && goalMatch;
  });

  const handleLearnMore = (program: Program) => {
    setSelectedProgram(program);
    setIsModalOpen(true);
  };

  const handleBookTrial = () => {
    window.open('https://forms.gle/9yvnUPvV2ifJin4S9', '_blank', 'noopener,noreferrer');
  };

  const handleResetFilters = () => {
    setSelectedAge('all');
    setSelectedLevel('all');
    setSelectedGoal('all');
    setRecommendedProgram(null);
  };

  const handleQuestionnaireComplete = (recommendation: string) => {
    setRecommendedProgram(recommendation);
    setIsQuestionnaireOpen(false);

    const programMap: Record<string, string> = {
      'Speed & Strength Training': 'speed',
      'Strength & Conditioning': 'strength',
      'General Fitness Program': 'fitness',
      'Kids Foundation Program': 'kids',
      'Senior Fitness Initiative': 'fitness'
    };

    setSelectedGoal(programMap[recommendation] || 'all');
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3"></div>
            <div className="h-32 bg-muted rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-muted rounded"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <section className="relative bg-gradient-to-br from-primary via-primary to-accent text-primary-foreground py-20">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              Discover Your Perfect Training Program
            </h1>
            <p className="text-lg md:text-xl text-primary-foreground/90 mb-8 leading-relaxed">
              From Olympic-level speed training to senior fitness initiatives, we offer comprehensive programs designed for every age, goal, and fitness level. Built on championship expertise and proven methodologies.
            </p>
            <button
              onClick={() => setIsQuestionnaireOpen(true)}
              className="px-8 py-4 bg-accent text-accent-foreground font-heading font-bold text-lg rounded-md hover:bg-accent/90 transition-all duration-300 shadow-hover inline-flex items-center space-x-2">

              <Icon name="SparklesIcon" size={24} variant="solid" />
              <span>Find My Program</span>
            </button>
          </div>
        </div>
      </section>

      <section className="w-full px-4 sm:px-6 lg:px-8 py-12">
        <ProgramFilter
          selectedAge={selectedAge}
          selectedLevel={selectedLevel}
          selectedGoal={selectedGoal}
          onAgeChange={setSelectedAge}
          onLevelChange={setSelectedLevel}
          onGoalChange={setSelectedGoal}
          onReset={handleResetFilters} />


        {recommendedProgram &&
        <div className="bg-accent/10 border border-accent rounded-lg p-4 mb-8 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Icon name="LightBulbIcon" size={24} variant="solid" className="text-accent" />
              <p className="text-sm font-heading font-semibold text-primary">
                Based on your answers, we recommend: <span className="text-accent">{recommendedProgram}</span>
              </p>
            </div>
            <button
            onClick={() => setRecommendedProgram(null)}
            className="text-accent hover:text-accent/80 transition-colors duration-300">

              <Icon name="XMarkIcon" size={20} variant="outline" />
            </button>
          </div>
        }

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredPrograms.map((program) =>
          <ProgramCard
            key={program.id}
            {...program}
            onLearnMore={() => handleLearnMore(program)} />

          )}
        </div>

        {filteredPrograms.length === 0 &&
        <div className="text-center py-12">
            <Icon name="FaceFrownIcon" size={48} variant="outline" className="text-text-secondary mx-auto mb-4" />
            <p className="text-text-secondary text-lg mb-4">No programs match your current filters</p>
            <button
            onClick={handleResetFilters}
            className="px-6 py-3 bg-accent text-accent-foreground font-heading font-bold text-sm rounded-md hover:bg-accent/90 transition-all duration-300">

              Reset Filters
            </button>
          </div>
        }

        <ComparisonMatrix />
      </section>

      <section className="w-full px-4 sm:px-6 lg:px-8 py-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary mb-6">
            Still Not Sure Which Program Is Right for You?
          </h2>
          <p className="text-text-secondary text-lg mb-8">
            Take our quick 2-minute questionnaire and get a personalized program recommendation based on your goals, experience, and lifestyle.
          </p>
          <button
            onClick={() => setIsQuestionnaireOpen(true)}
            className="px-8 py-4 bg-accent text-accent-foreground font-heading font-bold text-lg rounded-md hover:bg-accent/90 transition-all duration-300 shadow-card inline-flex items-center space-x-2">

            <Icon name="ClipboardDocumentCheckIcon" size={24} variant="solid" />
            <span>Take the Quiz</span>
          </button>
        </div>
      </section>

      <ProgramDetailModal
        program={selectedProgram}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onBookTrial={handleBookTrial} />


      <QuestionnaireModal
        isOpen={isQuestionnaireOpen}
        onClose={() => setIsQuestionnaireOpen(false)}
        onComplete={handleQuestionnaireComplete} />

    </div>);

};

export default ProgramsInteractive;